// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Exports the viewer's bundled `THREE` object and related types.
 *
 * Autodesk APS Viewer (aka Forgeviewer) goes through its bundled `window.THREE` (an older,
 * Autodesk-shipped build). It is incompatible at runtime with the npm `three` package our
 * `BendPreview/Labels` helpers import, so this should be used with the viewer and
 *  never the npm version of `'three'`.
 */

export const { THREE } = window;

/** A plain, THREE-build-agnostic 3D point. */
export type Point3D = { x: number; y: number; z: number };

/**
 * The camera exposed by `viewer.impl.camera`, narrowed to the perspective/orthographic fields the
 * leader-layout math reads. The viewer's UnifiedCamera sets these at runtime even though the base
 * `THREE.Camera` type does not declare them.
 */
export type ViewerCamera = THREE.Camera & {
  isPerspectiveCamera?: boolean;
  fov?: number;
  top?: number;
  bottom?: number;
};

/** Build a viewer `THREE.Vector3` from any plain point (defaults to the origin). */
export const toVector3 = (point?: Point3D | null): THREE.Vector3 =>
  point ? new THREE.Vector3(point.x, point.y, point.z) : new THREE.Vector3();

/** Reduce a viewer `THREE.Vector3` to a plain point (safe to persist / store in React state). */
export const toPoint3D = (vector: THREE.Vector3): Point3D => ({ x: vector.x, y: vector.y, z: vector.z });
